import { FC, useCallback, useEffect, useMemo } from 'react';

import RichText from '@eneco/components/RichText/RichText';
import { TrackedNotificationBox } from '@eneco/components/TrackedNotificationBox/TrackedNotificationBox';
import useDC from '@eneco/dc/useDC';
import { typedYupResolver } from '@eneco/hooks/form';
import { I18nProvider, useTranslation } from '@eneco/i18n';
import { Box, Button, Form, Grid, Heading, InputMeterReading, Skeleton, Stack, Text } from '@eneco/sparky';
import { InputMeterReadingProps } from '@eneco/sparky/types';
import { useTracking } from '@eneco/tracking';
import { Usages_Reading_CounterModel } from '@monorepo-types/dc';
import { FieldErrors, useForm } from 'react-hook-form';

import { CounterHint } from './components/CounterHint';
import { DeviationNotification } from './components/DeviationNotification';
import { meterReadingStepSchema } from './meterReadingStepSchema';
import { useMeterReadingsFlow } from '../state/MeterReadingsFlow.machine';
import { METER_READING_STATES } from '../state/MeterReadingsFlow.types';
import { Steps } from '../tracking';

export const MeterReadingStep: FC = () => {
  const { label } = useDC();

  return (
    <I18nProvider dictionary={locale => import(`./content/MeterReadingStep.${label}.${locale}.json`)}>
      <MeterReadingStepComponent />
    </I18nProvider>
  );
};

export type FormValues = {
  counters: Record<string, number>;
};

export const MeterReadingStepComponent: FC = () => {
  const { t, isLoading } = useTranslation();
  const { state, send } = useMeterReadingsFlow();
  const { trackFunnelStart, trackFunnelStep, scope } = useTracking();

  const stateValue = (state.value as METER_READING_STATES).METER_READINGS;
  const productType = stateValue.toLowerCase() as Lowercase<METER_READING_STATES['METER_READINGS']>;

  useEffect(() => {
    if (!state.context.funnelStarted) return;

    trackFunnelStep({
      step: Steps[`METER_READINGS.${stateValue}`],
      funnel: state.context.funnelName,
    });
  }, [state.context.funnelName, state.context.funnelStarted, stateValue, trackFunnelStep]);

  const handleOnFocus = () => {
    if (state.context.funnelStarted) return;
    const funnelName = `${scope} - adhoc`;
    trackFunnelStart({ step: Steps[`METER_READINGS.${stateValue}`], funnel: funnelName });
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const payload: any = { type: 'FUNNEL_START', funnelName };
    send(payload);
  };

  const counters = state.context.meters?.[productType]?.counters;
  const countersAmount = Number(counters?.length);

  const defaultValues = useMemo(
    () => ({
      counters: counters?.reduce(
        (counters: Record<string, unknown>, counter: Usages_Reading_CounterModel) => ({
          ...counters,
          [counter.counterIdentification]: counter.value,
        }),
        {},
      ),
    }),
    [counters],
  );

  const { handleSubmit, register, formState, control } = useForm<FormValues>({
    defaultValues,
    resolver: typedYupResolver<FormValues>(meterReadingStepSchema),
  });

  const counterErrors = formState.errors.counters as FieldErrors<FormValues['counters']>;

  const onSubmit = useCallback(
    (data: FormValues) => {
      const counterValues = counters?.map((counter: Usages_Reading_CounterModel) => ({
        ...counter,
        value: data.counters[counter.counterIdentification],
      }));

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const payload: any = {
        type: 'NEXT',
        meters: {
          [productType]: {
            counters: counterValues,
          },
        },
      };
      send(payload);
    },
    [counters, productType, send],
  );

  const getDenotation = (counter: Usages_Reading_CounterModel) =>
    counter.denotation === 'm3' ? 'm³' : counter.denotation;

  if (isLoading) return <Skeleton height={210} />;

  return (
    <Stack gap="6">
      <Stack gap="2">
        <Heading as="h1" size="M">
          {t(`${productType}Title`, { countersAmount })}
        </Heading>
        <RichText html={t(`${productType}Content`, { countersAmount })} />
      </Stack>
      <Box padding="4" backgroundColor="backgroundPrimary">
        <Stack gap="1">
          <Text size="BodyS">{t('addressLabel')}</Text>
          <Text size="BodyL" weight="bold">
            {state.context.addressLine}
          </Text>
        </Stack>
      </Box>
      <Form onFocus={handleOnFocus} onSubmit={handleSubmit(onSubmit)}>
        <Grid gap="6">
          {counters?.map((counter: Usages_Reading_CounterModel) => {
            return (
              <Stack key={counter.counterIdentification} gap="6">
                <Grid gridTemplateColumns={{ initial: '100%', md: '60%' }}>
                  <Grid.Item>
                    <InputMeterReading
                      {...register(`counters.${counter.counterIdentification}`)}
                      key={counter.counterIdentification}
                      label={counter.name || ''}
                      unit={counter.denotation as InputMeterReadingProps['unit']}
                      maximumFractionDigits={counter.positionsAfterComma}
                      hint={
                        <CounterHint productType={productType} counter={counter} denotation={getDenotation(counter)} />
                      }
                      error={
                        counterErrors?.[`counter${counter.counterIdentification}`]?.message &&
                        t('readingFormField.requiredText')
                      }
                      defaultValue={counter.value || ''}
                    />
                  </Grid.Item>
                </Grid>
                <DeviationNotification counter={counter} control={control} denotation={getDenotation(counter)} />
              </Stack>
            );
          })}
          {formState.errors.counters && (
            <Box paddingBottom="6">
              <TrackedNotificationBox
                isAlert={false}
                variant="warning"
                title={t(`readingMissingError.title`, { countersAmount })}
                text={<RichText html={t(`readingMissingError.content`, { countersAmount })} />}
              />
            </Box>
          )}
          <Stack.Item>
            <Button type="submit" isLoading={isLoading}>
              {t('submitButtonText')}
            </Button>
          </Stack.Item>
        </Grid>
      </Form>
    </Stack>
  );
};
